import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '../../../components/ui/Button';
import { Select } from '../../../components/ui/Select';
import { useAuth } from '../../../hooks/useAuth';
import { usePincode } from '../../../hooks/usePincode';

type PincodeType = 'prepaid' | 'cod' | 'both';

export const SettingPincode: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [pincodeType, setPincodeType] = useState<PincodeType>('prepaid');
  const [selectedCourier, setSelectedCourier] = useState<string>('');

  // Use the hook
  const {
    couriers = [],
    services = [],
    fetchCouriers,
    fetchServices,
    loading,
    error,
  } = usePincode();

  // Fetch all couriers on mount
  useEffect(() => {
    fetchCouriers();
  }, []);

  // Handler for the button
  const handleFetchServices = () => {
    if (!selectedCourier) {
      toast.error('Please select a courier first!');
      return;
    }
    fetchServices(selectedCourier);
  };

  return (
    <div className="py-4 lg:py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <button
            onClick={() => navigate(-1)}
            className="p-1 hover:bg-gray-100 rounded-full transition-colors"
          >
            <ArrowLeft className="w-6 h-6 text-gray-700" />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Pincode</h1>
        </div>

        <div className="flex items-center gap-4 text-gray-600">
          <span className="font-medium">
            {user?.firstName} {user?.lastName}, {user?.email}
          </span>
          <Button variant="link" className="text-primary p-0 h-auto">
            Go to profile
          </Button>
        </div>
      </div>

      {/* Card */}
      <div className="bg-card rounded-[16px] shadow-sm border p-10">
        <div className="flex flex-col gap-8">
          {/* Title */}
          <div className="border-b pb-4">
            <h2 className="text-xl font-semibold mb-1">
              Download Serviceable Pin-code
            </h2>
            <p className="text-sm text-gray-600">Active pin-code list</p>
          </div>

          {/* Form */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            {/* Select Courier */}
            <Select
              label="Select Courier"
              required
              value={selectedCourier}
              onChange={(e) => setSelectedCourier(e.target.value)}
              placeholder="Select Courier"
              options={couriers.map((c) => ({ label: c.name, value: c.id }))}
            />

            {/* Pincode Type */}
            <div className="flex flex-col gap-4">
              <label className="font-medium text-gray-900">Pin-code Type</label>

              <div className="flex items-center gap-10">
                {[
                  { id: 'prepaid', label: 'Prepaid' },
                  { id: 'cod', label: 'COD' },
                  { id: 'both', label: 'Both' },
                ].map((option) => (
                  <button
                    key={option.id}
                    onClick={() => setPincodeType(option.id as PincodeType)}
                    className="flex items-center gap-3 text-gray-700"
                  >
                    <span
                      className={`
                        h-4 w-4 rounded-full border
                        ${
                          pincodeType === option.id
                            ? 'border-accent bg-accent'
                            : 'border-gray-300'
                        }
                      `}
                    />
                    <span className="text-sm font-medium">{option.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Action */}
          <div className="flex justify-center pt-6">
            <Button className="px-10 py-3" onClick={handleFetchServices}>
              Get Pincodes
            </Button>
          </div>

          {/* Optional: show fetched services */}
          {services.length > 0 && (
            <div className="mt-6">
              <h3 className="text-lg font-semibold mb-2">Courier Services:</h3>
              <ul className="list-disc list-inside">
                {services.map((s) => (
                  <li key={s.id}>{s.name}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingPincode;
